import { TooltipWrapper } from 'components/TooltipWrapper';
import type { BimLimitedCostTypeEnum } from 'gql/graphql';
import {
    BimActKc2TotalCalcMethodEnum,
    BimCalculateInflationEnum,
    ContractStatusEnum,
    ProjectStatusEnum,
    WorkOrderStatusEnum,
} from 'gql/graphql';
import { Button, Layout, cnMixSpace, IconRevert } from 'uikit';

import { useCallback, useMemo } from 'react';

import type { IActTotalDetailsProps } from '../types';

import { ChoiceGroupHasTooltip } from './ChoiceGroupHasTooltip';

import Style from './ActKc2DetailsToolbox.module.css';

interface IToolbox {
    limitedTypes: BimLimitedCostTypeEnum[] | undefined;
    contractStatus?: ContractStatusEnum;
    workOrderStatus?: WorkOrderStatusEnum;
    statusProject?: ProjectStatusEnum;
    isVersion?: boolean;
    isActionDisabled?: boolean;
    onUseActKC2InflationValue: (actKC2Id: any, calculateInflation: BimCalculateInflationEnum) => void;
    inflationCalcType?: BimCalculateInflationEnum;
    calcMethod?: BimActKc2TotalCalcMethodEnum;
    hasDeflation?: boolean;
}

enum InflationCalcTypeEnum {
    systemCalculated = 'Рассчитано в системе',
    estimateSet = 'Установлено в смете',
}

const inflationCalcTypeItems = [InflationCalcTypeEnum.systemCalculated, InflationCalcTypeEnum.estimateSet];

export const ActKc2DetailsToolbox = ({
    limitedTypes,
    actKC2Id,
    contractStatus,
    updateLimitedHandler,
    resetCalculatedHandler,
    workOrderStatus,
    statusProject,
    isVersion,
    isActionDisabled,
    onUseActKC2InflationValue,
    inflationCalcType,
    calcMethod,
    hasDeflation,
}: Omit<IActTotalDetailsProps, 'setInputValueHandler'> & IToolbox) => {
    const isContractStatusDelayed = contractStatus === ContractStatusEnum.Delayed;
    const isContractStatusCompleted = contractStatus === ContractStatusEnum.Completed;

    const isWorkOrderStatusDelayed = workOrderStatus === WorkOrderStatusEnum.Delayed;
    const isWorkOrderStatusCompleted = workOrderStatus === WorkOrderStatusEnum.Completed;

    const isProjectStatusDelayed = statusProject === ProjectStatusEnum.Delayed;

    const inflationSelectDisabledByCalcMethod = useMemo(
        () => calcMethod === BimActKc2TotalCalcMethodEnum.FromXml,
        [calcMethod],
    );

    const inflationCalcTypeValue = useMemo(() => {
        if (inflationSelectDisabledByCalcMethod) {
            return null;
        }

        return inflationCalcTypeItems[inflationCalcType === BimCalculateInflationEnum.SystemCalculated ? 0 : 1];
    }, [inflationCalcType, inflationSelectDisabledByCalcMethod]);

    const inflationOptionTooltip = useMemo(() => {
        if (inflationSelectDisabledByCalcMethod) {
            return null;
        }

        return 'В загружаемом файле отсутствует дефлятор, отображение инфляции доступно только в состоянии “рассчитано в системе”';
    }, [inflationSelectDisabledByCalcMethod]);

    const actionsDisabled =
        isContractStatusDelayed ||
        isContractStatusCompleted ||
        isWorkOrderStatusDelayed ||
        isWorkOrderStatusCompleted ||
        isProjectStatusDelayed ||
        isVersion ||
        isActionDisabled;

    const onUpdateClickHandler = useCallback(
        (newState: boolean) => () => {
            if (limitedTypes) {
                updateLimitedHandler({
                    actKC2Id,
                    useLimited: newState,
                    limitedItems: limitedTypes,
                });
            }
        },
        [actKC2Id, limitedTypes, updateLimitedHandler],
    );

    const onResetAllClickHandler = useCallback(() => {
        resetCalculatedHandler?.({
            actKC2Id,
        });
    }, [actKC2Id, resetCalculatedHandler]);

    const onInflationCalcTypeChange = useCallback(
        (value: string) => {
            if (value !== inflationCalcTypeValue) {
                onUseActKC2InflationValue(
                    actKC2Id,
                    value === inflationCalcTypeItems[0]
                        ? BimCalculateInflationEnum.SystemCalculated
                        : BimCalculateInflationEnum.DefinedInPrice,
                );
            }
        },
        [actKC2Id, inflationCalcTypeValue, onUseActKC2InflationValue],
    );

    return (
        <Layout className={cnMixSpace({ mB: 's' })}>
            <Layout flex={1}>
                <div className={Style.headerButtonsBlock}>
                    <div className="title">Лимит. затраты:</div>
                    <Button
                        label="Вкл. все"
                        onClick={onUpdateClickHandler(true)}
                        view="ghost"
                        size="xs"
                        className={cnMixSpace({ mR: 's' })}
                        disabled={actionsDisabled}
                    />
                    <Button
                        label="Выкл. все"
                        onClick={onUpdateClickHandler(false)}
                        view="ghost"
                        size="xs"
                        disabled={actionsDisabled}
                    />
                </div>
                {inflationCalcType && (
                    <div className={Style.headerButtonsBlock}>
                        <div className="title">Инфляция:</div>
                        <TooltipWrapper
                            message="Недоступно при считывании итогов из файла"
                            size="m"
                            isInteractive={false}
                            isHidden={!inflationSelectDisabledByCalcMethod}
                        >
                            <ChoiceGroupHasTooltip
                                value={inflationCalcTypeValue}
                                onChange={onInflationCalcTypeChange}
                                items={inflationCalcTypeItems}
                                getItemDisabled={(item: string) =>
                                    inflationSelectDisabledByCalcMethod ||
                                    (!hasDeflation && item === InflationCalcTypeEnum.estimateSet)
                                }
                                tooltipMsg={inflationOptionTooltip}
                            />
                        </TooltipWrapper>
                    </div>
                )}
            </Layout>
            <Layout>
                <Button
                    label="Сбросить до исходных значений"
                    onClick={onResetAllClickHandler}
                    iconLeft={IconRevert}
                    view="clear"
                    size="xs"
                    disabled={actionsDisabled}
                />
            </Layout>
        </Layout>
    );
};

ActKc2DetailsToolbox.displayName = 'ActKc2DetailsToolbox';
